/**
 * AuditLogPageV2 — xem nhật ký hành động trên /v2/dat/* (ai đã làm gì).
 * Dữ liệu từ BE GET /v2/dat/action-logs.
 */
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import datV2 from '../../services/datV2';
import { Badge, Button, Form, Modal, Pagination, Spinner } from 'react-bootstrap';
import V2PageLayout from '../../components/v2/V2PageLayout';

// uniapp role id → nhãn hiển thị (mirror roles.constant.js). Chỉ để
// hiển thị trong modal chi tiết người thực hiện.
const ROLE_LABELS = {
  1: 'ADMIN',
  2: 'DRIVING_ADMIN',
  10: 'DRIVING.ADMIN',
  11: 'DRIVING.STUDENT',
  12: 'DRIVING.TEACHER',
  13: 'DRIVING.REPORTER',
  14: 'DRIVING.DRIVER',
  15: 'DRIVING.DAT_ADMIN',
  16: 'DRIVING.DAT_TEACHER',
  20: 'ELEARNING.ADMIN',
  99: 'SUPER_ADMIN',
};
const roleLabel = (r) => ROLE_LABELS[r] || `#${r}`;

// Render a single log-value for the change table. null/undefined → "∅".
const fmtVal = (v) => {
  if (v === null || v === undefined) return '∅';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'object') return JSON.stringify(v);
  const s = String(v);
  return s === '' ? '(trống)' : s;
};

// Is this `changes` value the new before→after diff shape
// ({ field: { from, to } })? Distinguishes it from legacy array / metadata.
const isDiffShape = (c) =>
  c != null &&
  typeof c === 'object' &&
  !Array.isArray(c) &&
  Object.values(c).every(
    (v) => v && typeof v === 'object' && !Array.isArray(v) && ('from' in v || 'to' in v),
  ) &&
  Object.keys(c).length > 0;

const ACTION_LABELS = {
  'update.session': { label: 'Cập nhật phiên', color: '#d1e7dd' },
  'update.student': { label: 'Cập nhật học viên', color: '#d1e7dd' },
  'delete.session': { label: 'Xoá phiên', color: '#f8d7da' },
  'bulk.upsert': { label: 'Import hàng loạt', color: '#cfe2ff' },
  'sync.ecotek': { label: 'Đồng bộ Ecotek', color: '#cfe2ff' },
  'sync.tp': { label: 'Đồng bộ TP', color: '#cfe2ff' },
  'sync.student': { label: 'Đồng bộ học viên', color: '#cfe2ff' },
  checkError: { label: 'Kiểm tra lỗi', color: '#fff3cd' },
  'tp.mark': { label: 'Đánh dấu TP', color: '#d1e7dd' },
  'sync.datResult': { label: 'Đồng bộ kết quả DAT', color: '#cfe2ff' },
  'cabin.create': { label: 'Cabin — Đặt lịch', color: '#e2d9f3' },
  'cabin.cancel': { label: 'Cabin — Hủy lịch', color: '#f8d7da' },
  'cabin.markAbsent': { label: 'Cabin — Vắng mặt', color: '#fff3cd' },
};

export default function AuditLogPageV2() {
  const gridRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  // Ô tra cứu. `qInput` là những gì đang gõ; `q` là thứ đã submit — tách
  // ra để mỗi phím gõ không bắn một request.
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  // Lọc theo 1 acc người thực hiện (click tên trong bảng để bật).
  // { actorId, actorName } | null.
  const [actorFilter, setActorFilter] = useState(null);

  // Performer-detail modal. `actor` holds the log row's actor snapshot
  // ({ actorId, actorName }); `actorDetail` is the resolved /user/:id doc.
  const [showActor, setShowActor] = useState(false);
  const [actor, setActor] = useState(null);
  const [actorDetail, setActorDetail] = useState(null);
  const [actorLoading, setActorLoading] = useState(false);
  const [actorError, setActorError] = useState(null);

  const openActorDetail = async (row) => {
    const actorId = row?.actorId;
    setActor({ actorId, actorName: row?.actorName });
    setActorDetail(null);
    setActorError(null);
    setShowActor(true);
    if (!actorId) {
      setActorError('Nhật ký này không ghi được người thực hiện (actorId trống).');
      return;
    }
    setActorLoading(true);
    try {
      const res = await datV2.getUserById(actorId);
      setActorDetail(res?.data || null);
    } catch (e) {
      console.error(e);
      setActorError(
        e?.response?.status === 404
          ? 'Không tìm thấy người dùng (có thể đã bị xoá).'
          : 'Không tải được thông tin người thực hiện.',
      );
    } finally {
      setActorLoading(false);
    }
  };

  // Student-detail modal. Danh tính học viên KHÔNG nằm trong log, chỉ
  // phân giải khi người dùng bấm nút (tránh N+1 request khi tải bảng).
  const [showStudent, setShowStudent] = useState(false);
  const [studentCode, setStudentCode] = useState(null);
  // /student/summary không trả họ tên — lấy từ phiên (hoặc log cabin).
  const [studentName, setStudentName] = useState(null);
  const [studentDetail, setStudentDetail] = useState(null);
  const [studentLoading, setStudentLoading] = useState(false);
  const [studentError, setStudentError] = useState(null);

  const openStudentDetail = async (row) => {
    setShowStudent(true);
    setStudentDetail(null);
    setStudentError(null);
    setStudentCode(null);
    setStudentName(null);
    setStudentLoading(true);
    try {
      // Mỗi loại log giữ mã học viên ở một chỗ khác nhau.
      let code = null;
      let name = null;
      if (row?.entityType === 'DatStudent') {
        code = row.entityId; // entityId chính là mã học viên
      } else if (row?.entityType === 'CabinBooking') {
        code = row?.changes?.maHocVien || null; // cabin nhúng sẵn mã + tên
        name = row?.changes?.tenHocVien || null;
      } else if (row?.entityType === 'DatSession') {
        // Log phiên chỉ có UUID → hỏi BE phiên đó của học viên nào.
        const res = await datV2.getSessionById(row.entityId);
        code = res?.data?.traineeRegistrationCode || null;
        name = res?.data?.traineeName || null;
      }
      setStudentName(name);
      if (!code) {
        setStudentError('Nhật ký này không gắn với học viên nào.');
        return;
      }
      setStudentCode(code);
      const sum = await datV2.getStudentSummary(code);
      setStudentDetail(sum?.data || null);
    } catch (e) {
      console.error(e);
      setStudentError(
        e?.response?.status === 404
          ? 'Không tìm thấy học viên (có thể đã bị xoá).'
          : 'Không tải được thông tin học viên.',
      );
    } finally {
      setStudentLoading(false);
    }
  };

  // Log nào có thể lần ra học viên — quyết định có hiện nút hay không.
  const canResolveStudent = (row) => {
    if (!row) return false;
    if (row.entityType === 'DatStudent' || row.entityType === 'DatSession') return !!row.entityId;
    if (row.entityType === 'CabinBooking') return !!row?.changes?.maHocVien;
    return false;
  };

  // Change-history modal. `changeRow` holds the selected log row; its
  // `changes` field can be one of three shapes (see renderChanges).
  const [showChanges, setShowChanges] = useState(false);
  const [changeRow, setChangeRow] = useState(null);

  const openChanges = (row) => {
    setChangeRow(row);
    setShowChanges(true);
  };

  const colDefs = useMemo(() => [
    {
      headerName: 'Thời gian',
      field: 'createdAt',
      width: 180,
      pinned: 'left',
      valueFormatter: (p) =>
        p.value ? new Date(p.value).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }) : '',
    },
    {
      headerName: 'Người thực hiện',
      field: 'actorName',
      width: 220,
      cellRenderer: (p) => {
        const label = p.value || p.data?.actorId || '—';
        if (!p.data) return label;
        return (
          <span className='d-inline-flex align-items-center gap-1'>
            <Button
              variant='link'
              size='sm'
              className='p-0 text-start'
              title='Lọc nhật ký theo người này'
              onClick={() => {
                setActorFilter({ actorId: p.data.actorId, actorName: p.data.actorName });
                setPage(1);
              }}
            >
              {label}
            </Button>
            <Button
              variant='link'
              size='sm'
              className='p-0 text-muted'
              title='Xem chi tiết người thực hiện'
              onClick={() => openActorDetail(p.data)}
            >
              ⓘ
            </Button>
          </span>
        );
      },
    },
    {
      headerName: 'Hành động',
      field: 'action',
      width: 160,
      valueFormatter: (p) => ACTION_LABELS[p.value]?.label || p.value || '—',
    },
    {
      headerName: 'Đối tượng',
      field: 'entityId',
      width: 360,
      cellRenderer: (p) => {
        const type = p.data?.entityType ? `[${p.data.entityType}] ` : '';
        const label = `${type}${p.value || '—'}`;
        if (!canResolveStudent(p.data)) return label;
        return (
          <span className='d-inline-flex align-items-center gap-2'>
            <span className='text-truncate'>{label}</span>
            <Button
              variant='link'
              size='sm'
              className='p-0 text-nowrap'
              title='Xem học viên liên quan đến nhật ký này'
              onClick={() => openStudentDetail(p.data)}
            >
              Học viên
            </Button>
          </span>
        );
      },
    },
    {
      headerName: 'Chi tiết',
      field: 'summary',
      flex: 1,
      minWidth: 300,
    },
    {
      headerName: 'Thay đổi',
      colId: 'changes',
      width: 130,
      pinned: 'right',
      cellRenderer: (p) => {
        if (!p.data) return null;
        const hasChanges =
          p.data.changes != null &&
          !(Array.isArray(p.data.changes) && p.data.changes.length === 0) &&
          !(typeof p.data.changes === 'object' && Object.keys(p.data.changes).length === 0);
        // Không có dữ liệu thay đổi (vd xoá phiên — soft delete, không có
        // diff before→after) → ẩn nút thay vì hiện nút mờ khó hiểu.
        if (!hasChanges) return null;
        return (
          <Button
            variant='outline-primary'
            size='sm'
            title='Xem lịch sử thay đổi'
            onClick={() => openChanges(p.data)}
          >
            Xem thay đổi
          </Button>
        );
      },
    },
  ], []);

  const fetchData = async (pg) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: 50 };
      if (actionFilter) params.action = actionFilter;
      if (from) params.from = from;
      if (to) params.to = to;
      if (q.trim()) params.q = q.trim();
      if (actorFilter?.actorId) params.actorId = actorFilter.actorId;
      const res = await datV2.getActionLogs(params);
      setRowData(res.data || []);
    } catch (e) {
      console.error(e);
      setRowData([]);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchData(page);
  }, [page, actionFilter, from, to, actorFilter, q]);

  const handleSearch = (e) => {
    e?.preventDefault();
    const next = qInput.trim();
    setPage(1);
    // Khi `q` đổi, useEffect ở trên đã fetch — gọi thêm ở đây sẽ thành 2
    // request trùng nhau. Chỉ tự fetch khi `q` không đổi (bấm "Lọc" lại).
    if (next === q) fetchData(1);
    else setQ(next);
  };

  return (
    <V2PageLayout
      title='Nhật ký thao tác DAT'
      filters={
        <>
          <Form.Control
            type='search'
            placeholder='Tìm mã HV, tên, người thực hiện...'
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(e); }}
            style={{ maxWidth: 260 }}
            title='Tìm trong nội dung nhật ký (mã đối tượng, mô tả, tên người thực hiện)'
          />
          <Form.Select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            style={{ maxWidth: 220 }}
          >
            <option value=''>Tất cả hành động</option>
            {Object.entries(ACTION_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </Form.Select>
          <Form.Control
            type='date'
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            style={{ maxWidth: 160 }}
          />
          <Form.Control
            type='date'
            value={to}
            onChange={(e) => setTo(e.target.value)}
            style={{ maxWidth: 160 }}
          />
          <Button onClick={handleSearch} variant='primary' disabled={loading}>
            {loading ? 'Đang tải...' : 'Lọc'}
          </Button>
          {actorFilter && (
            <Badge
              bg='info'
              className='d-inline-flex align-items-center gap-2'
              style={{ fontSize: '0.85rem' }}
            >
              Người: {actorFilter.actorName || actorFilter.actorId}
              <Button
                variant='link'
                size='sm'
                className='p-0 text-white text-decoration-none'
                title='Bỏ lọc theo người thực hiện'
                onClick={() => { setActorFilter(null); setPage(1); }}
              >
                ✕
              </Button>
            </Badge>
          )}
          {q && (
            <Badge
              bg='secondary'
              className='d-inline-flex align-items-center gap-2'
              style={{ fontSize: '0.85rem' }}
            >
              Từ khoá: {q}
              <Button
                variant='link'
                size='sm'
                className='p-0 text-white text-decoration-none'
                title='Xoá từ khoá tìm kiếm'
                onClick={() => { setQInput(''); setQ(''); setPage(1); }}
              >
                ✕
              </Button>
            </Badge>
          )}
          {q && (
            <div className='w-100 small text-muted mt-1'>
              Chỉ tìm trong nội dung nhật ký. Nhật ký “Cập nhật phiên” chỉ lưu mã phiên
              (không lưu tên/mã học viên) nên không khớp khi tìm theo học viên — dùng nút
              <strong> Học viên </strong> ở cột Đối tượng để xem học viên của phiên đó.
            </div>
          )}
        </>
      }
      actions={
        <Button
          variant='outline-secondary'
          onClick={() => fetchData(page)}
          disabled={loading}
          title='Tải lại dữ liệu trang hiện tại'
        >
          ⟳ Tải lại dữ liệu
        </Button>
      }
      footer={
        <Pagination className='mb-0'>
          <Pagination.First onClick={() => setPage(1)} disabled={loading || page === 1} />
          <Pagination.Prev onClick={() => setPage(page - 1)} disabled={loading || page === 1} />
          {page > 1 && (
            <Pagination.Item onClick={() => setPage(page - 1)}>{page - 1}</Pagination.Item>
          )}
          <Pagination.Item active>{page}</Pagination.Item>
          {rowData.length >= 50 && (
            <Pagination.Item onClick={() => setPage(page + 1)}>{page + 1}</Pagination.Item>
          )}
          <Pagination.Next
            onClick={() => setPage(page + 1)}
            disabled={loading || rowData.length < 50}
          />
        </Pagination>
      }
    >
      {loading && <p className='text-center mt-3'>Đang tải dữ liệu...</p>}
      {!loading && (
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={colDefs}
          getRowStyle={(params) => {
            const meta = ACTION_LABELS[params.data?.action];
            return meta ? { backgroundColor: meta.color } : undefined;
          }}
        />
      )}
      {!loading && rowData.length === 0 && (
        <p className='text-center mt-3'>Không có nhật ký nào.</p>
      )}

      <Modal show={showActor} onHide={() => setShowActor(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Thông tin người thực hiện</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actorLoading && (
            <div className='text-center py-3'>
              <Spinner animation='border' size='sm' /> Đang tải...
            </div>
          )}
          {!actorLoading && actorError && (
            <div>
              <p className='text-danger mb-2'>{actorError}</p>
              <dl className='row mb-0'>
                <dt className='col-4'>Tên (log)</dt>
                <dd className='col-8'>{actor?.actorName || '—'}</dd>
                <dt className='col-4'>User ID</dt>
                <dd className='col-8'>
                  <code>{actor?.actorId || '—'}</code>
                </dd>
              </dl>
            </div>
          )}
          {!actorLoading && !actorError && actorDetail && (
            <dl className='row mb-0'>
              <dt className='col-4'>Họ tên</dt>
              <dd className='col-8'>{actorDetail.name || actorDetail.userName || '—'}</dd>

              <dt className='col-4'>Tên đăng nhập</dt>
              <dd className='col-8'>{actorDetail.userName || '—'}</dd>

              <dt className='col-4'>Email</dt>
              <dd className='col-8'>{actorDetail.email || '—'}</dd>

              <dt className='col-4'>Điện thoại</dt>
              <dd className='col-8'>{actorDetail.phone || '—'}</dd>

              <dt className='col-4'>Zalo</dt>
              <dd className='col-8'>{actorDetail.zalo || '—'}</dd>

              <dt className='col-4'>Vai trò</dt>
              <dd className='col-8'>
                {(Array.isArray(actorDetail.role) ? actorDetail.role : [actorDetail.role])
                  .filter((r) => r != null)
                  .map((r) => (
                    <Badge bg='secondary' className='me-1' key={r}>
                      {roleLabel(r)}
                    </Badge>
                  ))}
              </dd>

              <dt className='col-4'>User ID</dt>
              <dd className='col-8'>
                <code>{actorDetail._id}</code>
              </dd>

              <dt className='col-4'>Tạo lúc</dt>
              <dd className='col-8'>
                {actorDetail.createdAt
                  ? new Date(actorDetail.createdAt).toLocaleString('vi-VN', {
                      timeZone: 'Asia/Ho_Chi_Minh',
                    })
                  : '—'}
              </dd>
            </dl>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowActor(false)}>
            Đóng
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showStudent} onHide={() => setShowStudent(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Thông tin học viên</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {studentLoading && (
            <div className='text-center py-3'>
              <Spinner animation='border' size='sm' /> Đang tải...
            </div>
          )}
          {!studentLoading && studentError && (
            <div>
              <p className='text-danger mb-2'>{studentError}</p>
              {studentCode && (
                <dl className='row mb-0'>
                  <dt className='col-4'>Mã học viên</dt>
                  <dd className='col-8'><code>{studentCode}</code></dd>
                </dl>
              )}
            </div>
          )}
          {!studentLoading && !studentError && studentDetail && (
            <dl className='row mb-0'>
              <dt className='col-5'>Mã học viên</dt>
              <dd className='col-7'><code>{studentDetail.registrationCode || studentCode}</code></dd>

              <dt className='col-5'>Họ tên</dt>
              <dd className='col-7'>{studentName || '—'}</dd>

              <dt className='col-5'>Hạng</dt>
              <dd className='col-7'>{studentDetail.license || '—'}</dd>

              <dt className='col-5'>Số phiên</dt>
              <dd className='col-7'>{studentDetail.sessionCount ?? '—'}</dd>

              <dt className='col-5'>Phiên gần nhất</dt>
              <dd className='col-7'>
                {studentDetail.lastSessionStartTime
                  ? new Date(studentDetail.lastSessionStartTime).toLocaleString('vi-VN', {
                      timeZone: 'Asia/Ho_Chi_Minh',
                    })
                  : '—'}
              </dd>

              <dt className='col-5'>Tổng quãng đường</dt>
              <dd className='col-7'>
                {studentDetail.totals?.totalDistance != null
                  ? `${Number(studentDetail.totals.totalDistance).toFixed(2)} km`
                  : '—'}
              </dd>

              <dt className='col-5'>Tổng thời gian</dt>
              <dd className='col-7'>
                {studentDetail.totals?.totalDuration != null
                  ? `${Number(studentDetail.totals.totalDuration).toFixed(2)} h`
                  : '—'}
              </dd>

              <dt className='col-5'>Đủ điều kiện</dt>
              <dd className='col-7'>
                <Badge bg={studentDetail.valid ? 'success' : 'secondary'}>
                  {studentDetail.valid ? 'Đạt' : 'Chưa đạt'}
                </Badge>
              </dd>

              <dt className='col-5'>Đã kiểm tra</dt>
              <dd className='col-7'>
                <Badge bg={studentDetail.allChecked ? 'success' : 'secondary'}>
                  {studentDetail.allChecked ? 'Đã kiểm tra' : 'Chưa kiểm tra'}
                </Badge>
              </dd>

              {studentDetail.message && (
                <>
                  <dt className='col-5'>Ghi chú</dt>
                  <dd className='col-7'>{studentDetail.message}</dd>
                </>
              )}
            </dl>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowStudent(false)}>
            Đóng
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showChanges} onHide={() => setShowChanges(false)} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>Lịch sử thay đổi</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='text-muted mb-1'>
            {ACTION_LABELS[changeRow?.action]?.label || changeRow?.action} —{' '}
            {changeRow?.entityType ? `[${changeRow.entityType}] ` : ''}
            {changeRow?.entityId || '—'}
          </p>
          <p className='mb-3'>{changeRow?.summary}</p>
          {(() => {
            const c = changeRow?.changes;
            if (c == null) return <p className='text-muted mb-0'>Không có dữ liệu thay đổi.</p>;

            // Shape 1 — before→after diff (new logs).
            if (isDiffShape(c)) {
              return (
                <table className='table table-sm table-bordered mb-0'>
                  <thead>
                    <tr>
                      <th>Trường</th>
                      <th>Giá trị cũ</th>
                      <th>Giá trị mới</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(c).map(([field, v]) => (
                      <tr key={field}>
                        <td className='fw-bold'>{field}</td>
                        <td className='text-danger'>{fmtVal(v.from)}</td>
                        <td className='text-success'>{fmtVal(v.to)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            }

            // Shape 2 — legacy update logs: array of changed field names
            // (values not recorded before this feature shipped).
            if (Array.isArray(c)) {
              return (
                <div>
                  <p className='mb-1'>Các trường đã thay đổi:</p>
                  <ul className='mb-2'>
                    {c.map((f) => (
                      <li key={f}>{String(f)}</li>
                    ))}
                  </ul>
                  <p className='text-muted small mb-0'>
                    Nhật ký cũ chỉ lưu tên trường, chưa có giá trị cũ → mới.
                  </p>
                </div>
              );
            }

            // Shape 3 — sync/bulk/other metadata object → key/value table.
            return (
              <table className='table table-sm table-bordered mb-0'>
                <tbody>
                  {Object.entries(c).map(([k, v]) => (
                    <tr key={k}>
                      <td className='fw-bold'>{k}</td>
                      <td>{fmtVal(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowChanges(false)}>
            Đóng
          </Button>
        </Modal.Footer>
      </Modal>
    </V2PageLayout>
  );
}
